import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const closeModal = () => setActiveModal(null);
  return <>
      <footer className="py-8 px-6 bg-cream border-t border-navy/5" data-id="element-193">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-194">
          <p className="text-navy/40 text-sm" data-id="element-195">
            © 2025 Cuando Todo Recae Sobre Ti. Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-196">
            <button onClick={() => setActiveModal('privacy')} className="text-navy/50 hover:text-terracotta text-sm transition-colors" data-id="element-197">
              Política de Privacidad
            </button>
            <span className="text-navy/20" data-id="element-198">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-navy/50 hover:text-terracotta text-sm transition-colors" data-id="element-199">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      {/* Modal Overlay */}
      <AnimatePresence data-id="element-200">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm" onClick={closeModal} data-id="element-201">
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 10,
          scale: 0.97
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-cream rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-202">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-navy/10" data-id="element-203">
                <h2 className="text-xl font-serif font-bold text-navy" data-id="element-204">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h2>
                <button onClick={closeModal} className="text-navy/40 hover:text-navy p-1 rounded-lg hover:bg-navy/5 transition-colors" aria-label="Cerrar" data-id="element-205">
                  <X className="w-5 h-5" data-id="element-206" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)] text-navy/80 text-sm leading-relaxed space-y-4" data-id="element-207">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-208" /> : <TermsContent data-id="element-209" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-xs text-navy/50 italic" data-id="element-210">
        Última actualización: febrero 2025
      </p>

      <h3 className="font-serif font-bold text-navy text-base mt-4" data-id="element-211">
        1. Información que recopilamos
      </h3>
      <p data-id="element-212">
        Al adquirir nuestro producto digital, recopilamos únicamente la
        información necesaria para procesar tu compra: nombre, dirección de
        correo electrónico y datos de pago. No almacenamos información de
        tarjetas de crédito; estos datos son procesados de forma segura por
        nuestro proveedor de pagos.
      </p>

      <h3 className="font-serif font-bold text-navy text-base mt-4" data-id="element-213">
        2. Uso de la información
      </h3>
      <p data-id="element-214">Utilizamos tu información personal exclusivamente para:</p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-215">
        <li data-id="element-216">Procesar y entregar tu compra digital.</li>
        <li data-id="element-217">Enviarte el enlace de descarga del producto.</li>
        <li data-id="element-218">Comunicarnos contigo en caso de problemas con tu pedido.</li>
      </ul>

      <h3 className="font-serif font-bold text-navy text-base mt-4" data-id="element-219">
        3. Protección de datos
      </h3>
      <p data-id="element-220">
        Implementamos medidas de seguridad razonables para proteger tu
        información personal contra acceso no autorizado, alteración o
        destrucción. No vendemos, intercambiamos ni transferimos tu información
        personal a terceros.
      </p>

      <h3 className="font-serif font-bold text-navy text-base mt-4" data-id="element-221">
        4. Cookies
      </h3>
      <p data-id="element-222">
        Este sitio puede utilizar cookies para mejorar la experiencia del
        usuario y analizar el tráfico web. Puedes configurar tu navegador para
        rechazar cookies, aunque esto podría afectar la funcionalidad del sitio.
      </p>

      <h3 className="font-serif font-bold text-navy text-base mt-4" data-id="element-223">
        5. Tus derechos
      </h3>
      <p data-id="element-224">
        Tienes derecho a acceder, rectificar o eliminar tus datos personales en
        cualquier momento. Para ejercer estos derechos, puedes contactarnos a
        través de los canales disponibles.
      </p>

      <h3 className="font-serif font-bold text-navy text-base mt-4" data-id="element-225">
        6. Cambios a esta política
      </h3>
      <p data-id="element-226">
        Nos reservamos el derecho de actualizar esta política de privacidad en
        cualquier momento. Los cambios serán publicados en esta página con la
        fecha de actualización correspondiente.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-xs text-navy/50 italic" data-id="element-227">
        Última actualización: febrero 2025
      </p>

      <h3 className="font-serif font-bold text-navy text-base mt-4" data-id="element-228">
        1. Producto digital
      </h3>
      <p data-id="element-229">
        "Cuando Todo Recae Sobre Ti" es un producto digital (PDF) de contenido
        educativo e informativo. No sustituye asesoría profesional médica,
        psicológica ni legal. El contenido se ofrece con fines orientativos.
      </p>

      <h3 className="font-serif font-bold text-navy text-base mt-4" data-id="element-230">
        2. Entrega
      </h3>
      <p data-id="element-231">
        Una vez completada la compra, recibirás acceso inmediato al archivo
        digital. La entrega se realiza de forma electrónica. Es tu
        responsabilidad proporcionar una dirección de correo electrónico válida.
      </p>

      <h3 className="font-serif font-bold text-navy text-base mt-4" data-id="element-232">
        3. Política de reembolso
      </h3>
      <p data-id="element-233">
        Debido a la naturaleza digital del producto, no se ofrecen reembolsos
        una vez que el archivo ha sido descargado o el acceso ha sido otorgado.
        Si experimentas problemas técnicos con la descarga, contáctanos y te
        ayudaremos a resolverlo.
      </p>

      <h3 className="font-serif font-bold text-navy text-base mt-4" data-id="element-234">
        4. Propiedad intelectual
      </h3>
      <p data-id="element-235">
        Todo el contenido del producto está protegido por derechos de autor. La
        compra otorga una licencia personal e intransferible. Queda prohibida la
        reproducción, distribución o reventa total o parcial del contenido sin
        autorización expresa.
      </p>

      <h3 className="font-serif font-bold text-navy text-base mt-4" data-id="element-236">
        5. Limitación de responsabilidad
      </h3>
      <p data-id="element-237">
        El autor y distribuidor no se hacen responsables de los resultados
        individuales obtenidos a partir de la aplicación del contenido. Los
        resultados pueden variar según las circunstancias personales de cada
        lector.
      </p>

      <h3 className="font-serif font-bold text-navy text-base mt-4" data-id="element-238">
        6. Modificaciones
      </h3>
      <p data-id="element-239">
        Nos reservamos el derecho de modificar estos términos en cualquier
        momento. Las modificaciones entrarán en vigor desde su publicación en
        esta página.
      </p>
    </>;
}